import { useCallback, useEffect, useRef, useState } from 'react';

export function useDraggable({ position, onChange, onCommit, threshold = 4, minMargin = 0 } = {}) {
  const [dragging, setDragging] = useState(false);
  const startRef = useRef(null);
  const movedRef = useRef(false);
  const positionRef = useRef(position);

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  const onPointerMove = useCallback((e) => {
    const start = startRef.current;
    if (!start) return;
    const dx = e.clientX - start.clientX;
    const dy = e.clientY - start.clientY;
    if (!movedRef.current && (Math.abs(dx) > threshold || Math.abs(dy) > threshold)) {
      movedRef.current = true;
    }
    if (!movedRef.current) return;
    if (typeof window === 'undefined') return;
    const w = window.innerWidth;
    const h = window.innerHeight;
    const base = start.position;
    const newRight = Math.max(minMargin, Math.min(w - 56, Math.round(base.right - dx)));
    const newBottom = Math.max(minMargin, Math.min(h - 56, Math.round(base.bottom - dy)));
    if (typeof onChange === 'function') onChange({ right: newRight, bottom: newBottom });
  }, [onChange, threshold, minMargin]);

  const onPointerUp = useCallback(() => {
    if (movedRef.current && typeof onCommit === 'function') {
      onCommit();
    }
    startRef.current = null;
    movedRef.current = false;
    setDragging(false);
  }, [onCommit]);

  useEffect(() => {
    if (!dragging) return undefined;
    if (typeof window === 'undefined') return undefined;
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [dragging, onPointerMove, onPointerUp]);

  const onPointerDown = useCallback((e) => {
    if (typeof e.button === 'number' && e.button !== 0) return;
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    const pos = positionRef.current || { right: 24, bottom: 24 };
    startRef.current = { clientX: e.clientX, clientY: e.clientY, position: { ...pos } };
    movedRef.current = false;
    setDragging(true);
  }, []);

  const didMove = useCallback(() => movedRef.current, []);

  return { onPointerDown, dragging, didMove };
}

export default useDraggable;
